/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Universal Multi-Tabs by Rana
(function () {
  function initSKBUniversalMultiTabs(scope = document) {
    const containers = scope.querySelectorAll(".skb_universal_tabs_wrap");

    containers.forEach((container) => {
      const tabs = container.querySelectorAll("[data-skb-tab-button]");
      const contents = container.querySelectorAll("[data-skb-tab-content]");
      const indicator = container.querySelector(".skb_tab_nav__position"); // Optional

      const tabsWrapper = container.querySelector(".skb_tabs");

      // Move indicator under active tab
      function moveIndicator(tab) {
        if (!indicator || !tab || !tabsWrapper) return;

        const tabOffsetLeft = tab.offsetLeft;
        const tabWidth = tab.offsetWidth;

        indicator.style.width = `${tabWidth}px`;
        indicator.style.transform = `translateX(${tabOffsetLeft}px)`;
      }

      // Set initial state
      const firstActiveTab = container.querySelector(".skb_tab.skb_active") || tabs[0];
      if (firstActiveTab) {
        firstActiveTab.classList.add("skb_active");
        moveIndicator(firstActiveTab);
        const firstTarget = firstActiveTab.getAttribute("data-skb-tab-button");
        const firstContent = container.querySelector(`[data-skb-tab-content="${firstTarget}"]`);
        if (firstContent) {
          firstContent.classList.add("skb_show");
          firstContent.style.position = "relative";
        }
      }

      // Add click events
      tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const target = tab.getAttribute("data-skb-tab-button");

          tabs.forEach((t) => t.classList.remove("skb_active"));
          tab.classList.add("skb_active");

          moveIndicator(tab);
          scrollActiveTabIntoView(tab);

          contents.forEach((content) => {
            content.classList.remove("skb_show");
            content.style.position = "absolute";
          });

          const next = container.querySelector(`[data-skb-tab-content="${target}"]`);
          if (next) {
            next.classList.add("skb_show");
            next.style.position = "relative";
          }
        });
      });

      // Recalculate on window resize or scroll
      if (indicator) {
        window.addEventListener("resize", () => {
          const activeTab = container.querySelector(".skb_tab.skb_active");
          if (activeTab) moveIndicator(activeTab);
        });

        // Sync position while scrolling horizontally
        if (tabsWrapper) {
          tabsWrapper.addEventListener("scroll", () => {
            const activeTab = container.querySelector(".skb_tab.skb_active");
            if (activeTab) moveIndicator(activeTab);
          });
        }
      }

      // Scroll tab into view if needed
      function scrollActiveTabIntoView(tab) {
        if (!tabsWrapper || !tab) return;

        const containerLeft = tabsWrapper.scrollLeft;
        const offsetLeft = tab.offsetLeft;
        const offsetRight = offsetLeft + tab.offsetWidth;

        const visibleLeft = containerLeft;
        const visibleRight = containerLeft + tabsWrapper.clientWidth;

        if (offsetLeft < visibleLeft) {
          tabsWrapper.scrollTo({
            left: offsetLeft - 40,
            behavior: "smooth",
          });
        } else if (offsetRight > visibleRight) {
          tabsWrapper.scrollTo({
            left: offsetRight - tabsWrapper.clientWidth + 40,
            behavior: "smooth",
          });
        }
      }
    });
  }

  // DOM ready
  document.addEventListener("DOMContentLoaded", () => {
    initSKBUniversalMultiTabs();
  });

  // Shopify section reload support
  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", (e) => {
      initSKBUniversalMultiTabs(e.target);
    });
  }
})();
// End of Universal Multi-Tabs

// Enforce max quantity in mini cart for AJAX increments
(function () {
  function getNextQuantityFromHref(href, fallbackHost) {
    try {
      var url = new URL(href, "https://" + (fallbackHost || window.location.host));
      var q = parseInt(url.searchParams.get("quantity"), 10);
      return isNaN(q) ? null : q;
    } catch (e) {
      return null;
    }
  }

  function clampMiniCartPlusLinks(scope) {
    var containers = (scope || document).querySelectorAll("line-item-quantity .quantity-selector");
    containers.forEach(function (container) {
      var input = container.querySelector("input.quantity-selector__input");
      var plus = container.querySelector("a.quantity-selector__button.ed_qtty_plus");
      if (!input || !plus) return;

      var maxAttr = input.getAttribute("max");
      if (!maxAttr) return;

      var max = parseInt(maxAttr, 10);
      if (!max || isNaN(max)) return;

      var current = parseInt(input.value || "0", 10) || 0;
      var nextFromHref = getNextQuantityFromHref(
        plus.getAttribute("href"),
        (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.host) || null
      );

      // Disable visual state if already at or above max
      if (current >= max) {
        plus.setAttribute("aria-disabled", "true");
      } else {
        plus.removeAttribute("aria-disabled");
      }

      // If the next qty encoded in the href would exceed max, clamp it
      if (nextFromHref != null && nextFromHref > max) {
        try {
          var url = new URL(
            plus.getAttribute("href"),
            "https://" + ((window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.host) || window.location.host)
          );
          url.searchParams.set("quantity", String(max));
          plus.setAttribute("href", url.pathname + "?" + url.searchParams.toString());
        } catch (e) {
          // ignore
        }
      }
    });
  }

  // Prevent clicks that would exceed max; run in capture to win over delegate handlers
  document.addEventListener(
    "click",
    function (event) {
      var plus = event.target && event.target.closest && event.target.closest("a.quantity-selector__button.ed_qtty_plus");
      if (!plus) return;

      var container = plus.closest(".quantity-selector");
      if (!container) return;

      var input = container.querySelector("input.quantity-selector__input");
      if (!input) return;

      var maxAttr = input.getAttribute("max");
      if (!maxAttr) return;

      var max = parseInt(maxAttr, 10);
      if (!max || isNaN(max)) return;

      var current = parseInt(input.value || "0", 10) || 0;
      var nextFromHref = getNextQuantityFromHref(
        plus.getAttribute("href"),
        (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.host) || null
      );

      // If already at max, block the increment entirely
      if (current >= max) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // If the next value would overshoot, clamp the link and let the theme handle it
      if (nextFromHref != null && nextFromHref > max) {
        try {
          var url = new URL(
            plus.getAttribute("href"),
            "https://" + ((window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.host) || window.location.host)
          );
          url.searchParams.set("quantity", String(max));
          plus.setAttribute("href", url.pathname + "?" + url.searchParams.toString());
        } catch (e) {
          // ignore
        }
      }
    },
    true
  );

  // Apply clamping on initial load and on cart refreshes (mini-cart re-render)
  document.addEventListener("DOMContentLoaded", function () {
    clampMiniCartPlusLinks();
  });
  document.documentElement.addEventListener("cart:refresh", function (e) {
    // Delay slightly to allow DOM replacement
    window.setTimeout(function () {
      clampMiniCartPlusLinks();
    }, 50);
  });
})();

// Sync sticky product quantity into main product form before submission
(function () {
  function syncQuantityFromSticky(submitter) {
    if (!submitter) return;

    var sticky = submitter.closest && submitter.closest('.product-sticky-form');
    if (!sticky) return;

    var formId = submitter.getAttribute('form');
    if (!formId) return;

    var form = document.getElementById(formId) || document.forms[formId];
    if (!form) return;

    var stickyQty = sticky.querySelector('input[name="quantity"]');
    if (!stickyQty) return;

    var value = stickyQty.value;

    // Update any quantity inputs inside the main form so FormData picks the intended value
    var formQtyInputs = form.querySelectorAll('input[name="quantity"]');
    if (formQtyInputs.length === 0) {
      // If form has no quantity input, create a hidden one
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.value = value;
      form.appendChild(hidden);
    } else {
      formQtyInputs.forEach(function (input) {
        try {
          input.value = value;
          // Trigger input/change so any listeners update internal state
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {}
      });
    }
  }

  // Delegate click from sticky submit buttons (runs before form submit handler)
  document.addEventListener(
    'click',
    function (event) {
      var submitter = event.target && event.target.closest && event.target.closest('.product-sticky-form [type="submit"][form]');
      if (!submitter) return;
      syncQuantityFromSticky(submitter);
    },
    true
  );

  // As a safety net, also run on form submit (capturing) when the submitter is within the sticky form
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[is="product-form"]')) return;

      var submitter = event.submitter || document.activeElement;
      if (!submitter) return;
      if (submitter.closest && submitter.closest('.product-sticky-form')) {
        syncQuantityFromSticky(submitter);
      }
    },
    true
  );
})();

// Toggle am_before_bg class when quick-buy-drawer opens/closes on desktop
(function () {
  let elementsWithAmBeforeBg = [];
  const DESKTOP_BREAKPOINT = 999;

  function isDesktop() {
    return window.innerWidth > DESKTOP_BREAKPOINT;
  }

  function handleDrawerOpen(event) {
    const drawer = event.target;
    if (!drawer || drawer.tagName !== 'QUICK-BUY-DRAWER' || !isDesktop()) {
      return;
    }

    elementsWithAmBeforeBg = Array.from(document.querySelectorAll('.am_before_bg'));
    
    elementsWithAmBeforeBg.forEach(function(element) {
      element.classList.remove('am_before_bg');
    });
  }

  function handleDrawerClose(event) {
    const drawer = event.target;
    if (!drawer || drawer.tagName !== 'QUICK-BUY-DRAWER' || !isDesktop()) {
      return;
    }

    elementsWithAmBeforeBg.forEach(function(element) {
      if (element && element.parentNode) {
        element.classList.add('am_before_bg');
      }
    });

    elementsWithAmBeforeBg = [];
  }

  document.addEventListener('openable-element:open', handleDrawerOpen);
  document.addEventListener('openable-element:close', handleDrawerClose);
})();

/**
 * PDP only
 * DE: (4,83 bei 12 Bewertungen) · EN: (4.83 based on 12 reviews).
 */

// (function () {
//   var PDP_REVIEWS_IO_AVG_DECIMALS = 2;

//   var ROOT = '.product-meta__reviews-badge .ruk_rating_snippet';
//   var debounceId;
//   var CLS = 'ruk-rating-snippet-count--pdp';

//   function isElaBeautyBrand() {
//     var b =
//       document.documentElement.getAttribute('data-brand') ||
//       document.body.getAttribute('data-brand') ||
//       '';
//     return b === 'ela-beauty';
//   }

//   function langDe() {
//     return (document.documentElement.getAttribute('lang') || '').toLowerCase().slice(0, 2) === 'de';
//   }

//   function getDecimals() {
//     if (typeof window.ElaReviewPdpAverageDecimals === 'number') {
//       return Math.max(0, Math.min(2, Math.round(window.ElaReviewPdpAverageDecimals)));
//     }
//     return Math.max(0, Math.min(2, PDP_REVIEWS_IO_AVG_DECIMALS));
//   }

//   function formatAverageDisplay(avgStr, decimals, useComma) {
//     var n = parseFloat(String(avgStr).replace(',', '.'));
//     if (isNaN(n)) {
//       return String(avgStr).trim();
//     }
//     var d = Math.max(0, Math.min(2, decimals));
//     var r = Math.round(n * Math.pow(10, d)) / Math.pow(10, d);
//     var s = r.toFixed(d);
//     return useComma ? s.replace('.', ',') : s;
//   }

//   /** Reviews.io uses "(16 Reviews)" — not "(16)" — so simple \(\d+\) parsing fails. */
//   function extractPdpReviewCount(text) {
//     var m = String(text).match(/\(\s*([\d.,\s]+)\s*reviews?\s*\)/i);
//     if (m) {
//       return toCountInt(m[1]);
//     }
//     m = String(text).match(/\(\s*([\d.,\s]+)\s*bewertungen\s*\)/i);
//     if (m) {
//       return toCountInt(m[1]);
//     }
//     m = String(text).trim().match(/\(\s*([0-9][0-9.,\s]*)\s*\)/);
//     return m ? toCountInt(m[1].replace(/\s/g, '')) : null;
//   }

//   function toCountInt(raw) {
//     if (/^\d{1,3}(\.\d{3})+/.test(raw)) {
//       return parseInt(raw.split(',')[0].replace(/\./g, ''), 10) || 0;
//     }
//     var digits = raw.replace(/[^\d]/g, '');
//     return digits ? parseInt(digits, 10) : 0;
//   }

//   function interpolatePdpTemplate(template, rating, count) {
//     if (!template) {
//       return '';
//     }
//     return String(template)
//       .replace(/\{\{\s*rating\s*\}\}/gi, rating)
//       .replace(/\{\{\s*count\s*\}\}/gi, String(count));
//   }

//   /** Ela Beauty PDP: strip merged/ raw snippet down to "(27)" only. */
//   function extractElaPdpCount(text) {
//     var t = String(text).trim();
//     if (/^\(\s*\d+\s*\)$/.test(t)) {
//       return { kind: 'done' };
//     }
//     var m = t.match(/bei\s+([\d.\s,]+)\s+Bewertung/i);
//     if (m) {
//       var dg = m[1].replace(/[^\d]/g, '');
//       return { kind: 'n', n: dg ? parseInt(dg, 10) : null };
//     }
//     m = t.match(/based on\s+(\d[\d,]*)\s+review/i);
//     if (m) {
//       return { kind: 'n', n: parseInt(m[1].replace(/,/g, ''), 10) };
//     }
//     var raw = extractPdpReviewCount(t);
//     if (raw !== null && raw >= 0) {
//       return { kind: 'n', n: raw };
//     }
//     return { kind: 'none' };
//   }

//   function formatPdpElaBeautyCountOnly(box, spans, countSpan, avgSpan) {
//     if (countSpan && avgSpan) {
//       var ci = extractPdpReviewCount(countSpan.textContent);
//       if (ci === null || ci < 0) {
//         return;
//       }
//       countSpan.textContent = '(' + ci + ')';
//       avgSpan.remove();
//       syncLabel(box, String(ci));
//       tagPdpCountSpans(box);
//       return;
//     }

//     if (spans.length === 1 && countSpan) {
//       var ex = extractElaPdpCount(countSpan.textContent);
//       if (ex.kind === 'done') {
//         tagPdpCountSpans(box);
//         syncLabel(box, countSpan.textContent.replace(/^\(|\)$/g, '').trim());
//         return;
//       }
//       if (ex.kind === 'n' && ex.n != null) {
//         countSpan.textContent = '(' + ex.n + ')';
//         syncLabel(box, String(ex.n));
//         tagPdpCountSpans(box);
//       }
//     }
//   }

//   function mergeLineFromStrings(avgStr, countInt, decimals) {
//     var useComma = langDe();
//     var rating = formatAverageDisplay(avgStr, decimals, useComma);
//     var strs = (window.themeVariables && window.themeVariables.strings) || {};
//     var tmpl =
//       countInt === 1
//         ? strs.reviewsIoPdpOne
//         : strs.reviewsIoPdpOther;
//     if (!tmpl) {
//       if (countInt === 1) {
//         tmpl = useComma ? '({{ rating }} bei 1 Bewertung)' : '({{ rating }} based on 1 review)';
//       } else {
//         tmpl = useComma
//           ? '({{ rating }} bei {{ count }} Bewertungen)'
//           : '({{ rating }} based on {{ count }} reviews)';
//       }
//     }
//     return interpolatePdpTemplate(tmpl, rating, countInt);
//   }

//   function tagPdpCountSpans(box) {
//     box.querySelectorAll('.ruk-rating-snippet-count').forEach(function (el) {
//       el.classList.add(CLS);
//     });
//   }

//   function syncLabel(box, text) {
//     var t = text.trim();
//     box.setAttribute('aria-label', t);
//     box.setAttribute('title', t);
//   }

//   function formatPdp(box) {
//     if (box.getAttribute('data-done') !== 'true' && box.getAttribute('data-retrieved') !== 'true') {
//       return;
//     }

//     var dec = getDecimals();
//     var spans = [].slice.call(box.querySelectorAll('.ruk-rating-snippet-count'));
//     var countSpan = null;
//     var avgSpan = null;
//     for (var i = 0; i < spans.length; i++) {
//       if (spans[i].querySelector('b')) {
//         avgSpan = spans[i];
//       } else if (!countSpan) {
//         countSpan = spans[i];
//       }
//     }

//     if (isElaBeautyBrand()) {
//       formatPdpElaBeautyCountOnly(box, spans, countSpan, avgSpan);
//       return;
//     }

//     if (
//       langDe() &&
//       countSpan &&
//       spans.length === 1 &&
//       /\sbei\s/.test(countSpan.textContent) &&
//       /Bewertung/.test(countSpan.textContent)
//     ) {
//       tagPdpCountSpans(box);
//       return;
//     }

//     if (
//       !langDe() &&
//       countSpan &&
//       spans.length === 1 &&
//       /\bbased on\b/i.test(countSpan.textContent) &&
//       /\breviews?\s*\)\s*$/i.test(countSpan.textContent)
//     ) {
//       tagPdpCountSpans(box);
//       return;
//     }

//     if (!countSpan || !avgSpan) {
//       return;
//     }

//     var countInt = extractPdpReviewCount(countSpan.textContent);
//     if (countInt === null || countInt < 0) {
//       return;
//     }

//     var b = avgSpan.querySelector('b');
//     if (!b) {
//       return;
//     }
//     var avgStr = b.textContent.trim();

//     countSpan.textContent = mergeLineFromStrings(avgStr, countInt, dec);
//     avgSpan.remove();
//     syncLabel(
//       box,
//       countSpan.textContent
//         .replace(/^\(|\)$/g, '')
//         .trim()
//     );
//     tagPdpCountSpans(box);
//   }

//   function run() {
//     document.querySelectorAll(ROOT).forEach(formatPdp);
//   }

//   function schedule() {
//     clearTimeout(debounceId);
//     debounceId = setTimeout(run, 100);
//   }

//   new MutationObserver(schedule).observe(document.documentElement, {
//     childList: true,
//     subtree: true,
//     characterData: true,
//     attributes: true,
//     attributeFilter: ['data-done', 'data-retrieved', 'data-sku']
//   });
//   document.addEventListener('shopify:section:load', run);
//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', run);
//   } else {
//     run();
//   }
// })();

/* START:Recharge PDP widget translations */
(function () {
  var ROOT_SELECTOR = 'recharge-subscription-widget, rc-learn-more-modal';
  var debounceId;
  var observedRoots = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  var textMap = [
    { from: /^Einmalig$/, to: 'One-time' },
    { from: /^Abo abschließen\s*&\s*sparen$/, to: 'Subscribe & save' },
    { from: /^Spare\s+(\d+)%$/, to: 'Save $1%' },
    { from: /^Lieferung alle:$/, to: 'Delivery every:' },
    { from: /^1 Monat$/, to: '1 month' },
    { from: /^(\d+) Monate$/, to: '$1 months' },
    { from: /^1 Woche$/, to: '1 week' },
    { from: /^(\d+) Wochen$/, to: '$1 weeks' },
    { from: /^1 Monat\s*:?\s*Spare\s+(\d+)%$/, to: '1 month : Save $1%' },
    { from: /^(\d+) Monate\s*:?\s*Spare\s+(\d+)%$/, to: '$1 months : Save $2%' },
    { from: /^1 Woche\s*:?\s*Spare\s+(\d+)%$/, to: '1 week : Save $1%' },
    { from: /^(\d+) Wochen\s*:?\s*Spare\s+(\d+)%$/, to: '$1 weeks : Save $2%' },
    { from: /^Kostenloser Versand ab 50 €$/, to: 'Free delivery on orders over €50' },
    {
      from: /^Nach der 2\. Lieferung jederzeit pausieren\s*&\s*kündigen$/,
      to: 'Pause & cancel anytime after the 2nd delivery'
    },
    { from: /^Kaufoptionen$/, to: 'Purchase options' },
    { from: /^Großartige Gründe für ein Abo$/, to: 'Great reasons to subscribe' },
    { from: /^Flexible Frequenz$/, to: 'Flexible frequency' },
    {
      from: /^Nicht sicher, wie viel du von etwas brauchst oder wie oft\? Passe Mengen und Frequenzen jederzeit an\.$/,
      to: 'Not sure how much of something you need, or how often? Adjust quantities and frequencies any time.'
    },
    { from: /^Bestellerinnerungen$/, to: 'Order reminders' },
    { from: /^Bestell-Erinnerungen$/, to: 'Order reminders' },
    {
      from: /^Wir informieren dich vor jeder Lieferung\. Verschiebe, ändere oder kündige bei Bedarf - wir berechnen nur, wenn deine Bestellung versendet wird\.$/,
      to: "We'll let you know before each shipment. Delay, reschedule or cancel if you need to - we'll only bill you when your order ships."
    },
    { from: /^Du hast die Kontrolle$/, to: "You're in control" },
    {
      from: /^Füge Abos hinzu oder entferne sie, storniere Bestellungen und bearbeite Frequenzen und Mengen über unser benutzerfreundliches Kundenportal\.$/,
      to: 'Add or remove subscriptions, cancel orders, and edit frequencies and quantities through our user-friendly customer portal.'
    },
    { from: /^Verstanden$/, to: 'Got it' }
  ];

  var attributeMap = {
    'Mehr erfahren': 'Learn more',
    Schließen: 'Close',
    'Großartige Gründe für ein Abo': 'Great reasons to subscribe',
    Kaufoptionen: 'Purchase options',
    'Lieferung alle:': 'Delivery every:'
  };

  var germanSelectOptionMap = [
    { from: /^1 month$/, to: '1 Monat' },
    { from: /^(\d+) months$/, to: '$1 Monate' },
    { from: /^1 week$/, to: '1 Woche' },
    { from: /^(\d+) weeks$/, to: '$1 Wochen' },
    { from: /^1 month\s*:?\s*Save\s+(\d+)%$/, to: '1 Monat : Spare $1%' },
    { from: /^(\d+) months\s*:?\s*Save\s+(\d+)%$/, to: '$1 Monate : Spare $2%' },
    { from: /^1 week\s*:?\s*Save\s+(\d+)%$/, to: '1 Woche : Spare $1%' },
    { from: /^(\d+) weeks\s*:?\s*Save\s+(\d+)%$/, to: '$1 Wochen : Spare $2%' }
  ];

  function getStorefrontLanguage() {
    return (document.documentElement.getAttribute('lang') || '').toLowerCase().slice(0, 2);
  }

  function isEnglishStorefront() {
    return getStorefrontLanguage() === 'en';
  }

  function isGermanStorefront() {
    return getStorefrontLanguage() === 'de';
  }

  function normalize(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  function translateText(text) {
    var trimmed = normalize(text);
    if (!trimmed) {
      return null;
    }

    for (var i = 0; i < textMap.length; i++) {
      if (textMap[i].from.test(trimmed)) {
        return trimmed.replace(textMap[i].from, textMap[i].to);
      }
    }

    return null;
  }

  function translateGermanSelectOption(text) {
    var trimmed = normalize(text);
    if (!trimmed) {
      return null;
    }

    for (var i = 0; i < germanSelectOptionMap.length; i++) {
      if (germanSelectOptionMap[i].from.test(trimmed)) {
        return trimmed.replace(germanSelectOptionMap[i].from, germanSelectOptionMap[i].to);
      }
    }

    return null;
  }

  function translateTextNodes(root) {
    if (!root || typeof document.createTreeWalker !== 'function') {
      return;
    }

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!normalize(node.nodeValue)) {
          return NodeFilter.FILTER_REJECT;
        }

        var parent = node.parentElement;
        if (parent && parent.closest && parent.closest('style, script')) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      }
    });

    var node;
    while ((node = walker.nextNode())) {
      var translated = translateText(node.nodeValue);
      if (translated) {
        var leading = String(node.nodeValue).match(/^\s*/)[0];
        var trailing = String(node.nodeValue).match(/\s*$/)[0];
        node.nodeValue = leading + translated + trailing;
      }
    }
  }

  function translateAttributes(root) {
    if (!root || !root.querySelectorAll) {
      return;
    }

    root.querySelectorAll('[aria-label], [title]').forEach(function (element) {
      ['aria-label', 'title'].forEach(function (attribute) {
        var value = normalize(element.getAttribute(attribute));
        var translated = attributeMap[value] || translateText(value);
        if (value && translated) {
          element.setAttribute(attribute, translated);
        }
      });
    });
  }

  function translateSelectOptions(root, language) {
    if (!root || !root.querySelectorAll) {
      return;
    }

    root.querySelectorAll('select option').forEach(function (option) {
      var translated = language === 'de' ? translateGermanSelectOption(option.text) : translateText(option.text);
      if (!translated) {
        return;
      }

      option.text = translated;
      option.label = translated;
      option.textContent = translated;
    });
  }

  function translateRoot(root, language) {
    if (language === 'de') {
      translateSelectOptions(root, language);
      return;
    }

    translateTextNodes(root);
    translateAttributes(root);
    translateSelectOptions(root, language);
  }

  function translateNestedShadowRoots(root, language) {
    if (!root || !root.querySelectorAll) {
      return;
    }

    root.querySelectorAll('*').forEach(function (child) {
      if (child.shadowRoot) {
        translateRoot(child.shadowRoot, language);
        observeRoot(child.shadowRoot);
        translateNestedShadowRoots(child.shadowRoot, language);
      }
    });
  }

  function translateRechargeRoot(element, language) {
    if (!element) {
      return;
    }

    translateRoot(element, language);

    if (element.shadowRoot) {
      translateRoot(element.shadowRoot, language);
      observeRoot(element.shadowRoot);
      translateNestedShadowRoots(element.shadowRoot, language);
    }
  }

  function run() {
    if (!isEnglishStorefront() && !isGermanStorefront()) {
      return;
    }

    var language = getStorefrontLanguage();
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (element) {
      translateRechargeRoot(element, language);
    });
  }

  function schedule() {
    clearTimeout(debounceId);
    debounceId = setTimeout(run, 50);
  }

  function observeRoot(root) {
    if (!root || !observedRoots || observedRoots.has(root)) {
      return;
    }

    observedRoots.add(root);
    new MutationObserver(schedule).observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-label', 'title', 'rc-selected', 'rc-not-selected', 'data-open']
    });
  }

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['lang', 'data-open']
  });

  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('variant:changed', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
/* END:Recharge PDP widget translations */
